import React, { useMemo, useState } from 'react';
import { showAlert, showConfirm, showPrompt } from '../../../common/AppDialog';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import {
  DataDefinition,
  DataDefinitionType,
  createFolder,
  refreshDefinition,
  moveDefinition,
  copyDefinition,
  deleteDefinition,
  deleteFolder,
} from '../../../../store/dataDefinitionsSlice';
import { typeLabelOf } from '../../nodeCatalog';
import { useTranslation } from 'react-i18next';
import IconWrapper from '../../../common/IconWrapper';
import { formatDateTime } from '../../../../utils/formatters';
import { findDefinitionUses } from '../../utils/definitionLink';
import { useDefinitionPropagation } from './useDefinitionPropagation';
import DataDefinitionHistoryDialog from './DataDefinitionHistoryDialog';
import {
  FiChevronRight, FiFolder, FiFileText, FiDatabase, FiFolderPlus, FiFilePlus, FiRefreshCw, FiPlusSquare, FiCheck, FiTrash2, FiEdit2, FiClock,
} from 'react-icons/fi';

interface TreeFolderNode {
  path: string;
  name: string;
  children: TreeFolderNode[];
  items: DataDefinition[];
}

function buildTree(folders: string[], items: DataDefinition[]): TreeFolderNode[] {
  const nodeMap = new Map<string, TreeFolderNode>();
  const roots: TreeFolderNode[] = [];
  const sorted = [...folders].sort((a, b) => a.split('/').length - b.split('/').length);
  sorted.forEach((path) => {
    const segments = path.split('/');
    const node: TreeFolderNode = { path, name: segments[segments.length - 1], children: [], items: [] };
    nodeMap.set(path, node);
    if (segments.length === 1) {
      roots.push(node);
    } else {
      const parentPath = segments.slice(0, -1).join('/');
      nodeMap.get(parentPath)?.children.push(node);
    }
  });
  items.forEach((item) => {
    nodeMap.get(item.path)?.items.push(item);
  });
  return roots;
}

/** 최상위 폴더(sam/db) 표시 이름 — 노드 유형 이름과 같다 */
const ROOT_TYPE: Record<string, string> = { sam: 'csvData', db: 'rdbData' };

interface Props {
  onRequestCreate?: (type: DataDefinitionType, folderPath: string) => void;
  /** 이 정의를 고치는 창을 연다 */
  onRequestEdit?: (item: DataDefinition) => void;
  /** 이 데이터를 지금 워크플로에 노드로 넣는다 — 넣었으면 true (열린 워크플로가 없으면 false) */
  onAddToWorkflow?: (item: DataDefinition) => boolean;
}

/** DBMS 종류 표시 이름 — 제품명이라 번역하지 않는다 */
const DBMS_NAME: Record<string, string> = {
  postgres: 'PostgreSQL', postgresql: 'PostgreSQL', mysql: 'MySQL', oracle: 'Oracle', tibero: 'Tibero', sqlite: 'SQLite',
};

/** 줄 끝 작은 버튼 — 워크플로 탐색기와 같은 모양(아이콘만, 올리면 옅은 바탕) */
const ROW_BTN = 'p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-200 shrink-0';
/** 삭제 버튼 — 탐색기와 같이 올리면 붉은 바탕 */
const DEL_BTN = 'p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-100 shrink-0';

/**
 * "데이터 정의" 탐색기: 그룹 폴더 = 상위, 데이터 정의 = 리프.
 * 검색 + 새 폴더/새 정의/갱신 + 이동(드래그)/복사(Ctrl+드래그, Ctrl+C/V) + 워크플로에 추가.
 *
 * 아이콘은 워크플로 탐색기와 같은 Feather 아이콘·크기를 쓴다. 예전에는 이모지(📁📄🗄, 📁+)를
 * 11px 글자로 써서 화면마다 모양이 달랐고 누르기 어려웠다(현장 2026-09-22).
 */
const DataDefinitionExplorer: React.FC<Props> = ({ onRequestCreate, onRequestEdit, onAddToWorkflow }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const folders = useSelector((state: RootState) => state.dataDefinitions.folders);
  const items = useSelector((state: RootState) => state.dataDefinitions.items);
  //: 지우기 전에 '지금 워크플로에서 이 정의를 쓰는 노드가 몇 개인지' 를 알려 주려고 본다
  const canvasNodes = useSelector((state: RootState) => state.workflow.nodes);
  const openWorkflowId = useSelector((state: RootState) => state.workflow.current?.id);
  //: 줄마다 부가 정보 — DB테이블은 어느 연결(DBMS)인지, SAM파일은 어느 SAM유형·경로인지
  const connections = useSelector((state: RootState) => state.connections.list);
  const samTypes = useSelector((state: RootState) => state.dataDefinitions.samTypes);
  const propagate = useDefinitionPropagation();
  /** 갱신·삭제 확인처럼 시간이 걸리는 일을 하는 중인 정의 — 그동안 그 줄의 버튼을 막는다 */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** 갱신 이력을 보고 있는 정의 */
  const [historyOf, setHistoryOf] = useState<DataDefinition | null>(null);

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['sam', 'db']));
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [activeFolderPath, setActiveFolderPath] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<string | null>(null);
  /** 방금 워크플로에 넣은 줄 — 잠깐 ✓ 로 바꿔 넣었다는 것을 보여 준다(창이 캔버스를 가리고 있어서) */
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const query = search.trim().toLowerCase();

  const visibleItems = useMemo(() => {
    if (!query) return items;
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(query) ||
        it.path.toLowerCase().includes(query) ||
        it.columns.some((c) => c.name.toLowerCase().includes(query))
    );
  }, [items, query]);

  const visibleFolders = useMemo(() => {
    if (!query) return folders;
    const keep = new Set<string>();
    folders.forEach((f) => {
      const nameMatches = (f.split('/').pop() || '').toLowerCase().includes(query);
      const hasMatchingItem = visibleItems.some((it) => it.path === f || it.path.startsWith(`${f}/`));
      if (nameMatches || hasMatchingItem) keep.add(f);
    });
    // 매치된 폴더의 상위 경로들도 트리가 끊기지 않도록 채워준다
    Array.from(keep).forEach((f) => {
      const segs = f.split('/');
      for (let i = 1; i < segs.length; i++) keep.add(segs.slice(0, i).join('/'));
    });
    return folders.filter((f) => keep.has(f));
  }, [folders, visibleItems, query]);

  const tree = useMemo(() => buildTree(visibleFolders, visibleItems), [visibleFolders, visibleItems]);

  const toggleExpand = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleDrop = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    if (e.ctrlKey) {
      dispatch(copyDefinition({ id, newPath: targetPath }));
    } else {
      dispatch(moveDefinition({ id, newPath: targetPath }));
    }
  };

  const handleNewSubfolder = async (parentPath: string) => {
    const name = await showPrompt(t('datadef:tree.newFolderPrompt'), { title: t('datadef:tree.newFolder'), placeholder: t('datadef:tree.folderName') });
    if (!name || !name.trim()) return;
    dispatch(createFolder(`${parentPath}/${name.trim()}`));
  };

  /** 정의 삭제 — 확인을 받고, 지금 워크플로에서 쓰고 있으면 그 수도 알려 준다 */
  const handleDeleteItem = async (item: DataDefinition) => {
    // 노드는 정의에 **연결**되어 있다 — 쓰는 곳이 있으면 지우지 않는다(SAM유형이 쓰이면 못 지우는 것과 같다).
    // 저장된 워크플로 전부와, 지금 편집 중이라 아직 저장하지 않은 캔버스까지 본다.
    setBusyId(item.id);
    let uses: Awaited<ReturnType<typeof findDefinitionUses>>;
    try {
      uses = await findDefinitionUses(item.id);
    } catch (err: any) {
      setBusyId(null);
      showAlert(t('datadef:tree.deleteFailed', { message: err?.message || String(err) }));
      return;
    }
    setBusyId(null);
    const openUse = canvasNodes.filter((n: any) => n.data?.config?.dataDefinitionId === item.id).length;
    const names = uses.uses.filter((u) => u.workflowId !== openWorkflowId).map((u) => `  · ${u.name} (${t('datadef:tree.nodeCount', { n: u.nodes })})`);
    if (openUse) names.unshift(`  · ${t('datadef:tree.openWorkflow')} (${t('datadef:tree.nodeCount', { n: openUse })})`);
    if (names.length) {
      showAlert([t('datadef:tree.deleteInUse', { name: item.name }), '', ...names, '', t('datadef:tree.deleteInUseHint')].join('\n'),
        { title: t('datadef:tree.deleteItem'), tone: 'warning' });
      return;
    }
    // 읽지 못한 워크플로가 있으면 '안 쓴다' 고 단정할 수 없다 — 그 사실을 알리고 묻는다
    const unsure = uses.failed.length ? `\n\n${t('datadef:tree.deleteUnsure', { n: uses.failed.length })}` : '';
    if (!(await showConfirm(t('datadef:tree.deleteItemConfirm', { name: item.name }) + unsure,
      { title: t('datadef:tree.deleteItem'), okText: t('datadef:tree.deleteOk'), tone: 'danger' }))) return;
    try {
      await dispatch(deleteDefinition(item.id)).unwrap();
      if (focusedId === item.id) setFocusedId(null);
      if (clipboard === item.id) setClipboard(null);
    } catch (err: any) {
      showAlert(t('datadef:tree.deleteFailed', { message: err?.message || String(err) }));
    }
  };

  /** 레이아웃 갱신 — 정의가 바뀌면 그 정의를 쓰는 노드 전부에 퍼뜨린다 */
  const handleRefresh = async (item: DataDefinition) => {
    setBusyId(item.id);
    try {
      await dispatch(refreshDefinition(item.id)).unwrap();
      await propagate([item.id]);
    } catch (err: any) {
      showAlert(t('datadef:tree.refreshFailed', { message: err?.message || String(err) }));
    } finally {
      setBusyId(null);
    }
  };

  /** 빈 폴더 삭제 — 정의가 남아 있으면 서버가 거절하고 그 사유를 보여 준다 */
  const handleDeleteFolder = async (path: string, name: string) => {
    if (!(await showConfirm(t('datadef:tree.deleteFolderConfirm', { name }), { title: t('datadef:tree.deleteFolder'), okText: t('datadef:tree.deleteOk'), tone: 'danger' }))) return;
    try {
      await dispatch(deleteFolder(path)).unwrap();
      if (activeFolderPath === path) setActiveFolderPath(null);
    } catch (err: any) {
      showAlert(t('datadef:tree.deleteFailed', { message: err?.message || String(err) }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const key = e.key.toLowerCase();
    // Delete 키: 고른 정의를 지운다 (검색칸에서 글자를 지우는 Delete 는 건드리지 않는다)
    if (key === 'delete' && focusedId && !(e.target instanceof HTMLInputElement)) {
      const it = items.find((i) => i.id === focusedId);
      if (it) { e.preventDefault(); handleDeleteItem(it); }
      return;
    }
    if ((e.ctrlKey || e.metaKey) && key === 'c' && focusedId) {
      e.preventDefault();
      setClipboard(focusedId);
    } else if ((e.ctrlKey || e.metaKey) && key === 'v' && clipboard) {
      e.preventDefault();
      const target = activeFolderPath || items.find((i) => i.id === clipboard)?.path;
      if (target) dispatch(copyDefinition({ id: clipboard, newPath: target }));
    }
  };

  /**
   * 이름 아래 한 줄 — 같은 이름의 정의가 여럿이어도 무엇을 가리키는지 알 수 있게.
   *   DB테이블: 연결 이름(DBMS) · 스키마.테이블 — 연결이 목록에 없으면 경고색으로 알린다
   *   SAM파일: SAM유형 · 파일 경로 — 유형을 쓰지 않는 정의는 '유형 없음'
   */
  const renderMeta = (item: DataDefinition) => {
    const src: any = item.source || {};
    const meta = 'truncate text-[0.6875rem] text-gray-500';
    if (item.type === 'db') {
      const conn = connections.find((c: any) => c.connection_id === src.connection);
      const dbms = conn ? DBMS_NAME[String(conn.conn_type || '').toLowerCase()] || conn.conn_type : '';
      const target = [src.schema, src.table].filter(Boolean).join('.');
      return (
        <span className={meta}>
          {src.connection
            ? <span className={conn ? '' : 'text-amber-600'} title={conn ? undefined : t('datadef:tree.connMissing')}>
                {src.connection}{dbms ? ` (${dbms})` : conn ? '' : ` — ${t('datadef:tree.connMissingShort')}`}
              </span>
            : <span className="text-amber-600">{t('datadef:tree.noConn')}</span>}
          {target && <span> · {target}</span>}
        </span>
      );
    }
    const typeName = src.samTypeId ? samTypes.find((s) => s.id === src.samTypeId)?.name : undefined;
    return (
      <span className={meta} title={src.filepath || undefined}>
        <span>{typeName || (src.samTypeId ? t('datadef:tree.samTypeMissing') : t('datadef:tree.noSamType'))}</span>
        <span> · {src.filepath || t('datadef:tree.noPath')}</span>
      </span>
    );
  };

  const addToWorkflow = (item: DataDefinition) => {
    if (!onAddToWorkflow?.(item)) return;
    setJustAdded(item.id);
    setTimeout(() => setJustAdded((cur) => (cur === item.id ? null : cur)), 1500);
  };

  const renderItemRow = (item: DataDefinition, depth: number) => (
    <div
      key={item.id}
      tabIndex={0}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', item.id)}  // 폴더 간 이동
      onFocus={() => setFocusedId(item.id)}
      onClick={() => setFocusedId(item.id)}
      onDoubleClick={() => addToWorkflow(item)}
      className={`flex items-center gap-1.5 px-1 py-1 rounded cursor-pointer text-sm ${
        focusedId === item.id ? 'bg-blue-50' : 'hover:bg-gray-50'
      } ${clipboard === item.id ? 'ring-1 ring-blue-300' : ''}`}
      style={{ paddingLeft: depth * 14 + 18 }}
      title={item.type === 'sam' ? item.source.filepath : `${item.source.connection} / ${item.source.schema ? `${item.source.schema}.` : ''}${item.source.table}`}
    >
      <span className="text-gray-500 shrink-0">
        <IconWrapper icon={item.type === 'sam' ? FiFileText : FiDatabase} size={14} />
      </span>
      <span className="flex-1 min-w-0 flex flex-col leading-tight">
        <span className="truncate text-gray-800">{item.name}</span>
        {renderMeta(item)}
      </span>
      <span className="text-[0.6875rem] text-gray-400 shrink-0">{t('datadef:tree.columns', { n: item.columns.length })}</span>
      {/* 최종 갱신일시 — 만들기·레이아웃 갱신·이동·SAM유형 반영 때 바뀐다(서버 updatedAt) */}
      <span className="text-[0.6875rem] text-gray-400 shrink-0 tabular-nums w-[7.25rem] text-right"
        title={t('datadef:tree.updatedAt', { time: formatDateTime(item.updatedAt) })}>
        {formatDateTime(item.updatedAt, { seconds: false })}
      </span>
      <button
        type="button"
        title={t('datadef:history.title')}
        onClick={(e) => { e.stopPropagation(); setHistoryOf(item); }}
        className={ROW_BTN}
      >
        <IconWrapper icon={FiClock} size={14} />
      </button>
      {onRequestEdit && (
        <button
          type="button"
          title={t('datadef:tree.edit')}
          onClick={(e) => { e.stopPropagation(); onRequestEdit(item); }}
          disabled={busyId === item.id}
          className={`${ROW_BTN} disabled:opacity-40`}
        >
          <IconWrapper icon={FiEdit2} size={14} />
        </button>
      )}
      <button
        type="button"
        title={t('datadef:tree.refreshTitle')}
        onClick={(e) => { e.stopPropagation(); handleRefresh(item); }}
        disabled={busyId === item.id}
        className={`${ROW_BTN} disabled:opacity-40`}
      >
        <span className={busyId === item.id ? 'inline-flex animate-spin' : 'inline-flex'}>
          <IconWrapper icon={FiRefreshCw} size={14} />
        </span>
      </button>
      <button
        type="button"
        title={t('datadef:tree.deleteItem')}
        onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }}
        disabled={busyId === item.id}
        className={`${DEL_BTN} disabled:opacity-40`}
      >
        <IconWrapper icon={FiTrash2} size={14} />
      </button>
      {onAddToWorkflow && (
        <button
          type="button"
          title={t('datadef:tree.addToWorkflow')}
          onClick={(e) => { e.stopPropagation(); addToWorkflow(item); }}
          className={justAdded === item.id
            ? 'p-1 rounded text-green-600 shrink-0'
            : 'p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 shrink-0'}
        >
          <IconWrapper icon={justAdded === item.id ? FiCheck : FiPlusSquare} size={16} />
        </button>
      )}
    </div>
  );

  const renderFolderNode = (node: TreeFolderNode, depth: number): React.ReactNode => {
    const isExpanded = query ? true : expanded.has(node.path);
    const label = depth === 0 && ROOT_TYPE[node.name] ? typeLabelOf(ROOT_TYPE[node.name]) : node.name;
    return (
      <div key={node.path}>
        <div
          className="flex items-center gap-1 px-1 py-1 rounded hover:bg-gray-100 cursor-pointer"
          style={{ paddingLeft: depth * 14 }}
          onClick={() => {
            toggleExpand(node.path);
            setActiveFolderPath(node.path);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => handleDrop(e, node.path)}
        >
          <span className={`tree-chevron text-gray-500 ${isExpanded ? 'open' : ''}`}>
            <IconWrapper icon={FiChevronRight} size={14} />
          </span>
          <span className="text-amber-500 shrink-0"><IconWrapper icon={FiFolder} size={14} /></span>
          <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-700">{label}</span>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              title={t('datadef:tree.newSubfolder')}
              onClick={(e) => { e.stopPropagation(); handleNewSubfolder(node.path); }}
              className={ROW_BTN}
            >
              <IconWrapper icon={FiFolderPlus} size={14} />
            </button>
            <button
              type="button"
              title={t('datadef:tree.newDefinition')}
              onClick={(e) => { e.stopPropagation(); onRequestCreate?.(node.path.split('/')[0] as DataDefinitionType, node.path); }}
              className={ROW_BTN}
            >
              <IconWrapper icon={FiFilePlus} size={14} />
            </button>
            {/* 최상위(SAM파일/DB테이블)는 지울 수 없다 — 버튼을 두지 않는다 */}
            {depth > 0 && (
              <button
                type="button"
                title={t('datadef:tree.deleteFolder')}
                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(node.path, node.name); }}
                className={DEL_BTN}
              >
                <IconWrapper icon={FiTrash2} size={14} />
              </button>
            )}
          </div>
        </div>
        {isExpanded && (
          <div>
            {node.children.map((child) => renderFolderNode(child, depth + 1))}
            {node.items.map((item) => renderItemRow(item, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" tabIndex={-1} onKeyDown={handleKeyDown}>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('datadef:tree.searchPh')}
        className="mb-2 w-full text-sm border border-gray-300 rounded px-2 py-1"
      />
      <div className="mb-1 text-[0.6875rem] text-gray-400">
        {t('datadef:tree.manageHint')}
      </div>
      <div className="flex-1 overflow-y-auto">
        {tree.map((node) => renderFolderNode(node, 0))}
        {tree.length === 0 && <div className="text-xs text-gray-400 px-1 py-2">{t('datadef:tree.empty')}</div>}
      </div>
      {historyOf && <DataDefinitionHistoryDialog item={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
};

export default DataDefinitionExplorer;
